import { STATUS_HUE } from "./statusColors";
import type { RenderService, ServiceSummary } from "./api";

/** Render deploy status → label + color. Covers the 11 Render statuses. */
export const DEPLOY_STATUS: Record<string, { label: string; color: string }> = {
  live: { label: "Live", color: STATUS_HUE.green },
  created: { label: "Queued", color: STATUS_HUE.amber },
  queued: { label: "Queued", color: STATUS_HUE.amber },
  build_in_progress: { label: "Building", color: STATUS_HUE.amber },
  update_in_progress: { label: "Deploying", color: STATUS_HUE.amber },
  pre_deploy_in_progress: { label: "Pre-deploy", color: STATUS_HUE.amber },
  build_failed: { label: "Build failed", color: STATUS_HUE.red },
  update_failed: { label: "Deploy failed", color: STATUS_HUE.red },
  pre_deploy_failed: { label: "Pre-deploy failed", color: STATUS_HUE.red },
  canceled: { label: "Canceled", color: STATUS_HUE.neutral },
  deactivated: { label: "Deactivated", color: STATUS_HUE.neutral },
};

export function deployStatus(status: string) {
  return DEPLOY_STATUS[status] ?? { label: status, color: STATUS_HUE.neutral };
}

type ServiceState = Pick<ServiceSummary, "suspended" | "latestDeploy">;

/** A service's own status: suspended wins over its last deploy. */
export function serviceStatus(s: ServiceState) {
  if (s.suspended) return { label: "Suspended", color: STATUS_HUE.neutral, inProgress: false };
  if (!s.latestDeploy) return null;
  return { ...deployStatus(s.latestDeploy.status), inProgress: s.latestDeploy.inProgress };
}

/** Failed, then mid-deploy, then idle, then live: the worst is what you need to see. */
function severity(s: ServiceState): number {
  if (s.suspended) return 1;
  const d = s.latestDeploy;
  if (!d) return -1;
  if (d.status.endsWith("_failed")) return 3;
  if (d.inProgress) return 2;
  return d.status === "live" ? 0 : 1;
}

/**
 * One status for a deployment: its worst service's, so a failed Keycloak
 * isn't hidden behind a live app. Ties go to the earlier (primary) service.
 */
export function groupStatus(services: ServiceState[]) {
  let worst: ServiceState | null = null;
  for (const s of services) {
    if (severity(s) > (worst ? severity(worst) : -1)) worst = s;
  }
  return worst ? serviceStatus(worst) : null;
}

/** "frankfurt · dev": the regions and branches across a deployment's services. */
export function groupMeta(services: Pick<RenderService, "region" | "branch">[]): string {
  const uniq = (xs: (string | null)[]) => [...new Set(xs.filter((x): x is string => !!x))];
  return [...uniq(services.map((s) => s.region)), ...uniq(services.map((s) => s.branch))].join(" · ");
}

/** "2m 30s" style duration from seconds. */
export function fmtDuration(secs: number | null): string {
  if (secs == null) return "";
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/** "2h ago" style relative time from an ISO string. */
export function timeAgo(iso: string | null): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
